import { FC, ReactNode, RefObject, useEffect, useRef, useState } from "react"

import { isClientSide } from "@utils"

// Thresholds work this way:
// First keyword = start
// Second keyword = end
// So, for example:
// BOTTOM_BOTTOM means when the top of the Ref hits the BOTTOM of the screen, start scrubbing.
// When the bottom of the Ref hits the BOTTOM of the screen, end scrubbing.
export enum SCRUBBER_THRESHOLDS {
  BOTTOM_BOTTOM = "BOTTOM_BOTTOM",
  BOTTOM_TOP = "BOTTOM_TOP",
}

const Scrubber: FC<{
  children: (ref: RefObject<any>, pctCompleted: number) => ReactNode
  threshold?: SCRUBBER_THRESHOLDS
}> = ({
  children,
  threshold = SCRUBBER_THRESHOLDS.BOTTOM_TOP
}) => {
    const ref = useRef<any>(null)
    const [pctCompleted, setPctCompleted] = useState<number>(0)

    useEffect(() => {
      if (!isClientSide()) return

      const handleScroll = () => {
        const rect = ref.current?.getBoundingClientRect()
        if (!rect) return

        const posFromBottom = window.innerHeight - rect.y
        const height = rect.height

        if (posFromBottom < 0) return

        if (threshold === SCRUBBER_THRESHOLDS.BOTTOM_BOTTOM) {
          if (posFromBottom >= height) {
            window.requestAnimationFrame(() => setPctCompleted(1))
            return
          }

          window.requestAnimationFrame(() => setPctCompleted(Math.ceil(posFromBottom / height * 1000) / 1000))
          return
        }

        if (threshold === SCRUBBER_THRESHOLDS.BOTTOM_TOP) {
          if (posFromBottom >= window.innerHeight + height) {
            window.requestAnimationFrame(() => setPctCompleted(1))
            return
          }

          window.requestAnimationFrame(() => setPctCompleted(Math.ceil(posFromBottom / (window.innerHeight + height) * 1000) / 1000))
          return
        }
      }

      window.addEventListener("scroll", handleScroll)

      return () => {
        window.removeEventListener("scroll", handleScroll)
      }
    }, [])

    return children(ref, pctCompleted)
  }

export default Scrubber
